import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { openFile, byteSize, Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './tour.reducer';

const BooleanIcon: React.FC<{ value: boolean }> = ({ value }) =>
  value ? <CheckCircleIcon sx={{ color: 'success.main' }} /> : <CancelIcon sx={{ color: 'error.main' }} />;

export const Tour = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const [filterState, setFilterState] = useState({
    id: '',
    code: '',
    enabled: '',
    badge: '',
  });

  const tourList = useAppSelector(state => state.tour.entities);
  const loading = useAppSelector(state => state.tour.loading);
  const totalItems = useAppSelector(state => state.tour.totalItems);

  const getAllEntities = () => {
    const filters = {
      page: paginationState.activePage - 1,
      size: paginationState.itemsPerPage,
      sort: `${paginationState.sort},${paginationState.order}`,
    };

    if (filterState.id) {
      Object.assign(filters, { id: filterState.id });
    }
    if (filterState.code) {
      Object.assign(filters, { code: filterState.code });
    }
    if (filterState.enabled !== '') {
      Object.assign(filters, { enabled: filterState.enabled });
    }
    if (filterState.badge) {
      Object.assign(filters, { badge: filterState.badge });
    }

    dispatch(getEntities(filters));
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleFilterChange = (field: string) => (event: any) => {
    setFilterState({
      ...filterState,
      [field]: event.target.value,
    });
  };

  const handleApplyFilter = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    getAllEntities();
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  const handleCreateClick = useCallback(() => {
    navigate('/tour/new');
  }, [navigate]);

  const CreateTourButton = useMemo(
    () =>
      memo(() => (
        <IconButton
          onClick={handleCreateClick}
          color="primary"
          id="jh-create-entity"
          data-cy="entityCreateButton"
          aria-label="Create new Tour"
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 48,
            height: 48,
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          <FontAwesomeIcon icon="plus" />
        </IconButton>
      )),
    [handleCreateClick],
  );

  return (
    <div>
      <h2 id="tour-heading" data-cy="TourHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.tour.home.title">Tours</Translate>
        <div className="d-flex justify-content-end">
          <Button
            variant="outlined"
            color="info"
            onClick={handleSyncList}
            disabled={loading}
            startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}
            sx={{ mr: 2 }}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.tour.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <CreateTourButton />
        </div>
      </h2>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Filter by ID"
          variant="outlined"
          size="small"
          value={filterState.id}
          onChange={handleFilterChange('id')}
          onKeyDown={e => e.key === 'Enter' && handleApplyFilter()}
          sx={{ width: 150 }}
        />
        <TextField
          label="Filter by Code"
          variant="outlined"
          size="small"
          value={filterState.code}
          onChange={handleFilterChange('code')}
          onKeyDown={e => e.key === 'Enter' && handleApplyFilter()}
          sx={{ width: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Enabled</InputLabel>
          <Select value={filterState.enabled} label="Enabled" onChange={handleFilterChange('enabled')}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Filter by Badge"
          variant="outlined"
          size="small"
          value={filterState.badge}
          onChange={handleFilterChange('badge')}
          onKeyDown={e => e.key === 'Enter' && handleApplyFilter()}
          sx={{ width: 150 }}
        />
        <Button variant="contained" color="primary" onClick={handleApplyFilter} sx={{ height: 40 }}>
          Apply
        </Button>
      </Box>
      <div className="table-responsive">
        {tourList && tourList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('enabled')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.enabled">Enabled</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('enabled')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('kind')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.kind">Kind</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('kind')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('mode')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.mode">Mode</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('mode')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.destination">Destination</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.defaultCategory">Default Category</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('duration')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.duration">Duration</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('duration')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('petFriendly')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.petFriendly">Pet Friendly</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('petFriendly')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('kidsAllowed')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.kidsAllowed">Kids Allowed</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('kidsAllowed')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('smoking')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.smoking">Smoking</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('smoking')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('app')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.app">App</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('app')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('accessibility')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.accessibility">Accessibility</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('accessibility')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('audioGuide')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.audioGuide">Audio Guide</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('audioGuide')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('tourGuide')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.tourGuide">Tour Guide</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('tourGuide')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('price')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.price">Price</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('price')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('badge')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.badge">Badge</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('badge')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tour.createdBy">Created By</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tourList.map((tour, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/tour/${tour.id}`} size="small" color="primary">
                        {tour.code}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.enabled} />
                    </TableCell>
                    <TableCell>
                      <Translate contentKey={`jhipsterSampleApplicationApp.TourKind.${tour.kind}`} />
                    </TableCell>
                    <TableCell>
                      <Translate contentKey={`jhipsterSampleApplicationApp.TourMode.${tour.mode}`} />
                    </TableCell>
                    <TableCell>
                      {tour.destination ? <Link to={`/destination/${tour.destination.id}`}>{tour.destination.code}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {tour.defaultCategory ? (
                        <Link to={`/tour-category/${tour.defaultCategory.id}`}>{tour.defaultCategory.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>
                      {tour.duration} <Translate contentKey={`jhipsterSampleApplicationApp.DurationMeasure.${tour.durationMeasure}`} />
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.petFriendly} />
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.kidsAllowed} />
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.smoking} />
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.app} />
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.accessibility} />
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.audioGuide} />
                    </TableCell>
                    <TableCell>
                      <BooleanIcon value={tour.tourGuide} />
                    </TableCell>
                    <TableCell>{tour.price}</TableCell>
                    <TableCell>{tour.badge}</TableCell>
                    <TableCell>
                      {tour.createdDate ? <TextFormat type="date" value={tour.createdDate} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>{tour.createdBy ? tour.createdBy.login : ''}</TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button
                          component={Link}
                          to={`/tour/${tour.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          startIcon={<FontAwesomeIcon icon="pencil-alt" />}
                        >
                        </Button>
                        <Button
                          onClick={() =>
                            navigate(
                              `/tour/${tour.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`,
                            )
                          }
                          color="error"
                          startIcon={<FontAwesomeIcon icon="trash" />}
                        >
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.tour.home.notFound">No Tours found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={tourList && tourList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Tour;
